'use client';

import { usePathname, useRouter, useParams, useSearchParams } from 'next/navigation';
import React, { createContext, useCallback, useMemo, useState } from 'react';
import Language from '@/enums/Language';

interface LanguageContextState {
	currentLocale: Language;
	handleLanguageChange: (selectedLanguage: Language) => void;
}

export const LanguageContext = createContext<LanguageContextState>({
	currentLocale: Language.EN,
	handleLanguageChange: () => {},
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
	const pathname = usePathname();
	const router = useRouter();
	const params = useParams();
	const searchParams = useSearchParams();
	const defaultLocale = (params.lang as Language) ?? Language.EN;

	const [currentLocale, setCurrentLocale] = useState<Language>(defaultLocale);

	const handleLanguageChange = useCallback(
		(selectedLanguage: Language) => {
			setCurrentLocale(selectedLanguage ?? Language.EN);

			const changeLanguagePath = pathname
                ? pathname.replace(pathname.split('/')[1] || '', selectedLanguage)
                : '';
			const existingParams = searchParams.toString()?.length ? `?${searchParams.toString()}` : '';

			router.push(`${changeLanguagePath}${existingParams}`);
		},
		[pathname, router, searchParams],
	);

	const contextState = useMemo(() => {
		return { currentLocale, handleLanguageChange };
	}, [currentLocale, handleLanguageChange]);

	return <LanguageContext.Provider value={contextState}>{children}</LanguageContext.Provider>;
}
